import {TermFilter} from '@dofleini/query-builder';
import {CubaCIRuler, isPassword, LastNameRuler, NameRuler, ValidatorProvider} from '@dofleini/validator';
import {users} from '@mep/pae-commons';
import {Tag} from 'antd';
import cn from 'classnames';
import i18n from 'i18next';
import isObject from 'lodash/isObject';

import MunicipalitySelect from '@/components/MunicipalitySelect';
import PhoneEditor from '@/components/PhoneNumberField/PhoneEditor';
import Phones from '@/components/PhoneNumberField/PhonesListView';
import ProvinceSelect from '@/components/ProvinceSelect';
import StringListField from '@/components/StringListField';
import renderAvatarViewWidget from '@/components/UserAvatar/renderAvatarViewWidget';
import {MIN_PASSWORD_SCORE_STRENGTH} from '@/constants';
import {ipAndMaskRegexRuler} from '@/lib/validator/ipValidation';
import {renderFieldPassword} from '@/modules/admin/components/FieldPassword';
import UserAccessLevelSelect from '@/modules/admin/components/UserAccessLevelSelect';
import {renderAvatarSection} from '@/modules/admin/components/UserDetailContent/AvatarSection';
import UserOfficeSelection from '@/modules/admin/components/UserOfficeSelection';
import {renderUserProposalType} from '@/modules/admin/components/UserProposalType';
import UserRoles from '@/modules/admin/components/UserRoles';
import UserTypeSelect from '@/modules/admin/components/UserTypeSelect';
import {useSearchRol} from '@/modules/admin/hooks/useSearchRol';
import {useProposalRequestPagination} from '@/modules/proposalRequest/containers/ProposalRequestEntityContainer';
import {handleSocialDenomination} from '@/modules/proposalRequest/hooks/useSocialDenominationType';
import ProposalRequestApiService from '@/modules/proposalRequest/services/ProposalRequestApiService';
import createClearRenderView from '@/utils/createClearRenderView';
import createFormFieldset from '@/utils/createFormFieldset';
import {createRenderRemoteEntity} from '@/utils/createRenderRemoteEntity';
import {createSearchFieldRemote} from '@/utils/createSearchFieldRemote';
import {validations} from '@/utils/fields';
import {renderMunicipality, renderProvince} from '@/utils/location';

const {USER_TYPES} = users;

export const baseFields = [
  {
    key: 'avatar',
    colSpan: 2,
    onlyView: true,
    renderView: renderAvatarViewWidget
  },
  {
    key: 'avatar',
    colSpan: 2,
    render: renderAvatarSection,
    onlyEdit: true,
    label: 'user.form.avatar.placeholder'
  },
  {
    key: 'firstName',
    colSpan: 1,
    label: 'user.form.firstName.placeholder',
    onlyEdit: true,
    required: true,
    rules: [NameRuler],
  },
  {
    key: 'middleName',
    colSpan: 1,
    label: 'user.form.middleName.placeholder',
    onlyEdit: true,
    rules: [NameRuler],
  },
  {
    key: 'lastName',
    colSpan: 1,
    label: 'user.form.lastName.placeholder',
    onlyEdit: true,
    required: true,
    rules: [LastNameRuler],
  },
  {
    key: 'secondName',
    colSpan: 1,
    label: 'user.form.secondName.placeholder',
    onlyEdit: true,
    required: false,
    rules: [LastNameRuler],
  },
  {
    key: 'idNumber',
    colSpan: 2,
    label: 'user.form.idNumber.placeholder',
    rules: [{required: true}, CubaCIRuler]
  },
  {
    key: 'phone',
    label: 'phones',
    required: false,
    widget: PhoneEditor,
    viewWidget: Phones,
    widgetProps: {
      haveType: true
    },
    viewWidgetProps: {
      columns: true,
      useLink: false
    },
    className: 'form-view-mode-vertical-item',
    rules: [validations.listPhones(), validations.phoneValidator()],
  },
  {
    key: 'email',
    colSpan: 2,
    label: 'user.form.email.placeholder',
    renderView: createClearRenderView(),
    rules: [{required: true}, {type: 'email'}],
  },
  {
    key: 'address.province',
    required: false,
    colSpan: 1,
    label: 'province',
    widget: ProvinceSelect,
    viewWidget: ({value}) => renderProvince(value)
  },
  {
    key: 'address.municipality',
    label: 'municipality',
    required: false,
    onlyEdit: true,
    colSpan: 1,
    related: 'address.province',
    relatedKey: 'province',
    widget: MunicipalitySelect
  },
  {
    key: 'address.municipality',
    onlyView: true,
    label: 'municipality',
    viewWidget: ({value}) => renderMunicipality(value)
  },
  {
    key: 'lastProposal',
    colSpan: 2,
    label: 'lastProposal',
    renderView: createRenderRemoteEntity({
      service: ProposalRequestApiService.getOne,
      render: createClearRenderView(null, (e) => handleSocialDenomination(e)),
    }),
    widget: createSearchFieldRemote({
      usePagination: useProposalRequestPagination,
      fetchValueFunc: ProposalRequestApiService.getOne,
      fullEntity: true,
      keyLabel: 'socialDenomination',
      size: 10,
      getValue: (value, keyValue) => isObject(value) ? value[keyValue] || value['code'] : value,
      mapResponse: (v) => {
        return v?.map(e => ({
          ...e,
          socialDenomination: handleSocialDenomination(e)
        }));
      }
    }),
    className: 'form-view-mode-vertical-item'
  },
  {
    key: 'level',
    label: 'accessLevel.label',
    renderView: createClearRenderView('', v => ValidatorProvider.t(`common:accessLevel.${v}`)),
    widget: UserAccessLevelSelect,
    onlyEdit: true,
    required: false
  }
];

const fields = [
  ...baseFields,
  {
    key: 'userType',
    colSpan: 2,
    label: 'user.form.userType.placeholder',
    renderView: createClearRenderView('', v => ValidatorProvider.t(`admin:user.form.userType.options.${v}`)),
    widget: UserTypeSelect,
    onlyEdit: true,
    related: 'metadata.office',
    initialValue: USER_TYPES.USER
  },
  {
    key: 'metadata.office',
    colSpan: 2,
    label: 'user.form.office.placeholder',
    widget: UserOfficeSelection,
    related: 'userType',
    onlyEdit: true,
    propsByRelated: (relatedValue, props, values) => {
      if (!relatedValue || relatedValue === USER_TYPES.USER) {
        return {
          ...props,
          className: cn(props.className, 'hidden')
        };
      }
      
      return {
        ...props,
        widgetProps: {
          userType: relatedValue,
          filters: values?.address?.province ? new TermFilter({
            field: 'address.province',
            value: values?.address?.province
          }).toQuery() : {}
        }
      };
    }
  },
  {
    key: 'subjectType',
    label: 'user.form.proposalSubjectType',
    colSpan: 2,
    onlyEdit: true,
    render: renderUserProposalType,
  },
  {
    key: 'security',
    colSpan: 2,
    onlyEdit: true,
    render: createFormFieldset({title: 'user.form.security', translation: 'admin'}),
  },
  {
    key: 'roles',
    colSpan: 2,
    label: 'user.form.roles',
    widget: createSearchFieldRemote({
      usePagination: useSearchRol,
      mode: 'multiple',
      // eslint-disable-next-line no-unused-vars,react/display-name,react/prop-types
      tagRender: ({label, ...props}) => {
        const exist = i18n.exists(`admin:rol.${label}`);
        const title = exist ? i18n.t(`admin:rol.${label}`) : label;
        return <Tag {...props} className={'uppercase px-2 py-1 my-1'}>{title}</Tag>;
      },
      customMapFunc: (role) => {
        const exist = i18n.exists(`admin:rol.${role?.label}`);
        const title = exist ? i18n.t(`admin:rol.${role?.label}`) : role?.label;
        return {
          ...role,
          label: title || role?.label
        };
      },
      fullEntity: true
    }),
    viewWidget: UserRoles
  },
  {
    key: 'createdAt',
    label: 'registerAt',
    type: 'date',
    onlyView: true
  },
  {
    key: 'password',
    label: 'setNewPassword',
    colSpan: 2,
    onlyEdit: true,
    widget: renderFieldPassword,
    rules: [{
      validator: (_, value) => {
        if (!value || isPassword(value, MIN_PASSWORD_SCORE_STRENGTH)) {
          return Promise.resolve();
        }
        const message = _.message || 'passStrength';
        return Promise.reject(ValidatorProvider.t(message));
      },
    }]
  },
  {
    key: 'ips',
    label: 'user.form.ipWhiteList.label',
    colSpan: 2,
    onlyEdit: true,
    widgetProps: {
      rules: [ipAndMaskRegexRuler]
    },
    widget: StringListField,
  },
];

export {fields};
